import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { signOutAsks, signOutOptions } from '../lib/signOutOptions'
import { hasBrowserAccount, signOutAndLand, signOutOfMicrosoftToo, type AuthMode } from '../lib/teams'
import { useFocusTrap } from '../lib/useFocusTrap'
import { Button, ModalBackdrop, dialogPanelClass } from './ui'

/** The one Sign out: with a Microsoft session in this browser it asks whether to end that too, otherwise it acts at once. */
export function SignOutButton({ host }: { host: AuthMode | null }) {
  const { t } = useTranslation()
  const [choosing, setChoosing] = useState(false)
  const [leaving, setLeaving] = useState(false)

  function press() {
    if (signOutAsks(signOutOptions({ host, hasMicrosoftAccount: hasBrowserAccount() }))) setChoosing(true)
    else {
      setLeaving(true)
      void signOutAndLand()
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={press}
        disabled={leaving}
        aria-busy={leaving || undefined}
        className="shrink-0 text-xs font-medium text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-slate-100"
      >
        {t('app.signOut')}
      </button>
      {choosing && <SignOutChoice onCancel={() => setChoosing(false)} />}
    </>
  )
}

/** Its own component so the focus trap mounts with the dialog, not with the button. */
function SignOutChoice({ onCancel }: { onCancel: () => void }) {
  const { t } = useTranslation()
  const trapRef = useFocusTrap<HTMLDivElement>()
  const [leaving, setLeaving] = useState(false)

  function leave() {
    setLeaving(true)
    void signOutAndLand()
  }

  return (
    <ModalBackdrop ref={trapRef} onClose={onCancel} aria-labelledby="sign-out-choice-title">
      <div className={dialogPanelClass}>
        <h2 id="sign-out-choice-title" className="text-base font-semibold text-slate-900 dark:text-slate-100">
          {t('app.signOut')}
        </h2>
        <p className="text-sm text-slate-700 dark:text-slate-300">{t('app.signOutChoiceBody')}</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" loading={leaving} onClick={leave}>
            {t('app.signOut')}
          </Button>
          <Button disabled={leaving} onClick={() => void signOutOfMicrosoftToo()}>
            {t('app.signOutMicrosoft')}
          </Button>
          <Button variant="ghost" disabled={leaving} onClick={onCancel}>
            {t('action.cancel')}
          </Button>
        </div>
      </div>
    </ModalBackdrop>
  )
}
